import * as React from "react";
import { AddConnectionSinglePage } from "./AddConnectionSinglePage";
import { Toast } from "./components";
import { ConnectionDetail } from "./ConnectionDetail";
import { ContactDetail } from "./ContactDetail";
import { MainPane } from "./MainPane";
import { OfficeAdapter } from "./office";
import { TYPE_VISUAL } from "./icons";
import { useSession } from "./SessionContext";
import { APP_VERSION } from "../version";
import {
  bulkCreateConnections,
  createCaseFromEmail,
  fetchConnectionsForEmail,
  fetchEmailByInternetMessageId,
  reimportEmailFromOutlook,
} from "./api/client";
import { entityFromLinkable } from "./api/mappers";
import type {
  ConnectableType,
  CreateConnectionInput,
  GqlModelConnection,
} from "./api/types";
import type { Connection, EmailContext, Entity, EntityKind, Route } from "./types";

/** Map the UI's EntityKind to the backend's ConnectableType enum. */
function toConnectableType(kind: EntityKind): ConnectableType | null {
  switch (kind) {
    case "case": return "CASE";
    case "task": return "TASK";
    case "note": return "NOTE";
    case "contact": return "CONTACT";
    case "business": return "BUSINESS";
    case "account": return "ACCOUNT";
    case "address": return "ADDRESS";
    case "car": return "CAR";
    case "rental": return "CAR_RENTAL";
    case "asset": return "ASSET";
    case "tracking": return "TRACKING_NUMBER";
    // Generic kinds — now first-class ConnectableType endpoints (the backend
    // exposes them via App\GraphQL\Support\ConnectableTypeMap). Each maps to
    // the matching UPPER_SNAKE enum value.
    case "trip": return "TRIP";
    case "correspondence": return "CORRESPONDENCE";
    case "phone_call": return "PHONE_CALL";
    case "sms": return "SMS_MESSAGE";
    case "finance_card": return "FINANCE_CARD";
    case "finance_creditline": return "FINANCE_CREDITLINE";
    case "aircraft": return "AIRCRAFT";
    case "legal_entity": return "LEGAL_ENTITY";
    case "employment": return "EMPLOYMENT";
    case "tag": return "TAG";
    case "pnr": return "PASSENGER_NAME_RECORD";
    case "calendar_event": return "EXCHANGE_CALENDAR_EVENT";
    case "medical_encounter": return "MEDICAL_ENCOUNTER";
    // "all" is the search filter, not a real entity — not connectable.
    default: return null;
  }
}

export function App({ onSignOut }: { onSignOut: () => void }): JSX.Element {
  const session = useSession();
  const accessToken = session?.accessToken ?? null;
  const refreshSession = session?.refreshSession;

  const [email, setEmail] = React.useState<EmailContext | null>(null);
  const [epicEmailId, setEpicEmailId] = React.useState<string | null>(null);
  // null = still loading; [] = loaded, no connections; otherwise = the rows.
  // The distinction lets MainPane avoid flashing "No connections yet" while
  // we're actually still fetching.
  const [connections, setConnections] = React.useState<Connection[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [route, setRoute] = React.useState<Route>({ name: "main" });
  const [justAddedId, setJustAddedId] = React.useState<string | null>(null);
  const [toastMsg, setToastMsg] = React.useState<string | null>(null);
  // Import state for an email that isn't in EPIC yet. `importing` drives the
  // spinner; `importError` holds the server's reason on a failed/ not-found
  // reimport so MainPane can show it with a retry.
  const [importing, setImporting] = React.useState(false);
  const [importError, setImportError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    // Clear any stale import state from a previous render/email before we
    // re-resolve the current one.
    setImporting(false);
    setImportError(null);
    (async () => {
      try {
        await OfficeAdapter.ready();
        const ctx = await OfficeAdapter.getEmailContext(accessToken, refreshSession);
        if (cancelled) return;
        setEmail(ctx);

        if (accessToken && ctx.internetMessageId) {
          try {
            const em = await fetchEmailByInternetMessageId(
              ctx.internetMessageId,
              accessToken,
              refreshSession
            );
            if (cancelled) return;
            if (em) {
              setEpicEmailId(em.id);
              const gql = await fetchConnectionsForEmail(
                em.id,
                accessToken,
                refreshSession
              );
              if (cancelled) return;
              setConnections(mapEmailConnections(gql, em.id));
            } else {
              // Email not in EPIC yet — no connections possible. Mark
              // resolved-empty so the loading state ends.
              setConnections([]);
            }
          } catch (e: any) {
            console.warn("Connections fetch failed:", e?.message ?? e);
            if (!cancelled) setConnections([]);
          }
        } else {
          // No token or no IMID — nothing to fetch; resolve to empty.
          setConnections([]);
        }
      } catch (e: any) {
        if (cancelled) return;
        setLoadError(e?.message ?? String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const flashToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2400);
  };

  const handleAddConnection = (initialTab?: import("./connTabs").ConnTab) => {
    setRoute({ name: "add-single", tab: initialTab ?? "all" });
  };

  const handleCreateCaseFromEmail = async () => {
    if (!accessToken || !epicEmailId) {
      throw new Error("Email is not yet tracked in EPIC.");
    }

    try {
      // Single-field input — server runs AI on the email body to fill
      // name + remarks, and auto-creates the EmailMessage↔Case connection.
      const newCase = await createCaseFromEmail(
        { email_message_id: epicEmailId },
        accessToken,
        refreshSession
      );

      // Refresh so the new Case connection appears in MainPane.
      const gql = await fetchConnectionsForEmail(
        epicEmailId,
        accessToken,
        refreshSession
      );
      setConnections(mapEmailConnections(gql, epicEmailId));

      setRoute({ name: "main" });
      flashToast(`Case ${newCase.reference_number} created`);
    } catch (e: any) {
      flashToast(`Create case failed: ${e?.message ?? "Unknown error"}`);
      throw e;
    }
  };

  const handleImportEmail = async () => {
    const imid = email?.internetMessageId;
    if (!accessToken || !imid) {
      setImportError("This email can't be imported — it has no Internet Message ID.");
      return;
    }
    setImporting(true);
    setImportError(null);
    try {
      // Synchronous on the server — it searches every mailbox over live Graph,
      // so this await can take several seconds. A "not in any mailbox" outcome
      // comes back as success:false (not a throw); we show result.message then.
      const result = await reimportEmailFromOutlook(imid, accessToken, refreshSession);
      if (result.success && result.email) {
        setEpicEmailId(result.email.id);
        const gql = await fetchConnectionsForEmail(
          result.email.id,
          accessToken,
          refreshSession
        );
        setConnections(mapEmailConnections(gql, result.email.id));
        flashToast("Email imported");
      } else {
        setImportError(
          result.success
            ? "Imported, but the record couldn't be loaded. Try again."
            : result.message || "This email couldn't be found in any mailbox."
        );
      }
    } catch (e: any) {
      setImportError(e?.message ?? "Import failed. Check your connection and try again.");
    } finally {
      setImporting(false);
    }
  };

  const handleConnect = async (entities: Entity[]) => {
    // Everything runs inside the try so EVERY failure path toasts — including
    // the validation throws below. (Previously those threw before the try, so
    // selecting an unconnectable kind made Connect fail with zero feedback:
    // no toast, no console, no request.)
    try {
      // Connect button shouldn't be reachable unless the email is in EPIC
      // (the Connections section is hidden until then). Guard anyway.
      if (!accessToken || !epicEmailId) {
        throw new Error("Email is not yet tracked in EPIC.");
      }

      // Build CreateConnectionInput per selected entity. Generic kinds
      // without a ConnectableType counterpart are dropped — they can be
      // surfaced in search but the mutation can't accept them today.
      const inputs: CreateConnectionInput[] = [];
      const dropped: string[] = [];
      for (const e of entities) {
        const t = toConnectableType(e.kind);
        if (!t) {
          dropped.push(e.title);
          continue;
        }
        inputs.push({
          entity_one_type: "EMAIL",
          entity_one_id: epicEmailId,
          entity_two_type: t,
          entity_two_id: e.id,
          disallow_duplicates: true,
        });
      }
      if (inputs.length === 0) {
        throw new Error(
          dropped.length === 1
            ? `"${dropped[0]}" is a record type the add-in can't link yet.`
            : "Those record types can't be linked from the add-in yet."
        );
      }

      const result = await bulkCreateConnections(inputs, accessToken, refreshSession);

      // Refresh the connections list so the new rows appear in MainPane.
      const gql = await fetchConnectionsForEmail(epicEmailId, accessToken, refreshSession);
      setConnections(mapEmailConnections(gql, epicEmailId));

      setRoute({ name: "main" });

      const created = result.created;
      const skipped = result.skipped_existing;
      const noun = (n: number) => (n === 1 ? "record" : "records");
      let msg =
        skipped > 0
          ? `Linked ${created} ${noun(created)} (${skipped} already linked)`
          : `Linked ${created} ${noun(created)}`;
      // If a mixed selection had some unconnectable kinds, say so rather than
      // silently dropping them.
      if (dropped.length > 0) msg += `; ${dropped.length} not linkable yet`;
      flashToast(msg);
    } catch (e: any) {
      flashToast(`Connect failed: ${e?.message ?? "Unknown error"}`);
      // Re-throw so AddConnection can exit its loading state but keep
      // selections so the user can retry.
      throw e;
    }
  };

  const existingEntityIds = React.useMemo(
    () => new Set((connections ?? []).map((c) => c.entity.id)),
    [connections]
  );

  if (loadError) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          color: "var(--ios-secondary)",
          lineHeight: 1.5,
        }}
      >
        {loadError}
      </div>
    );
  }
  if (!email) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--ios-secondary)" }}>
        Loading email…
      </div>
    );
  }

  let body: JSX.Element;
  if (route.name === "main") {
    body = (
      <MainPane
        email={email}
        connections={connections}
        emailInEpic={epicEmailId !== null}
        importing={importing}
        importError={importError}
        onImportEmail={handleImportEmail}
        justAddedId={justAddedId}
        onAddConnection={handleAddConnection}
        onOpenConnection={(id) => setRoute({ name: "connection-detail", connectionId: id })}
        onOpenContact={(id) => setRoute({ name: "contact-detail", contactId: id })}
        onShowToast={flashToast}
      />
    );
  } else if (route.name === "add-single") {
    body = (
      <AddConnectionSinglePage
        key={`single-${route.tab}`}
        email={email}
        epicEmailMessageId={epicEmailId}
        existingConnectionEntityIds={existingEntityIds}
        initialTab={route.tab}
        onCancel={() => setRoute({ name: "main" })}
        onConnect={handleConnect}
        onCreateCaseFromEmail={handleCreateCaseFromEmail}
      />
    );
  } else if (route.name === "connection-detail") {
    const conn = (connections ?? []).find((c) => c.id === route.connectionId);
    if (!conn) {
      body = <div className="pane">Connection not found.</div>;
    } else {
      body = (
        <ConnectionDetail
          connection={conn}
          onBack={() => setRoute({ name: "main" })}
        />
      );
    }
  } else if (route.name === "contact-detail") {
    const allContacts = [email.from, ...email.to, ...email.cc];
    const contact = allContacts.find((c) => c.id === route.contactId);
    if (!contact) {
      body = <div className="pane">Contact not found.</div>;
    } else {
      body = (
        <ContactDetail
          key={contact.id}
          contact={contact}
          onBack={() => setRoute({ name: "main" })}
        />
      );
    }
  } else {
    body = <div className="pane">Unknown route.</div>;
  }

  return (
    <>
      {body}
      {toastMsg && <Toast>{toastMsg}</Toast>}
      <div className="footer">
        <span>
          {session?.username}
          <span className="footer-version"> · v{APP_VERSION}</span>
        </span>
        <button onClick={onSignOut}>Sign out</button>
      </div>
    </>
  );
}

/**
 * Convert GraphQL ModelConnection rows (where one side is the open email) into
 * the UI's Connection[] shape. The "other" side becomes the displayed Entity.
 */
function mapEmailConnections(
  rows: GqlModelConnection[],
  emailId: string
): Connection[] {
  const out: Connection[] = [];
  for (const mc of rows) {
    const e1 = mc.entity_one;
    const e2 = mc.entity_two;
    const e1Entity = e1 ? entityFromLinkable(e1) : null;
    const e2Entity = e2 ? entityFromLinkable(e2) : null;
    const isE1Email = e1?.__typename === "Email" && e1.id === emailId;
    const isE2Email = e2?.__typename === "Email" && e2.id === emailId;
    // "Other" side = whichever isn't the open email. Used as the
    // displayed entity in the connections list.
    const otherEntity = isE1Email ? e2Entity : isE2Email ? e1Entity : (e2Entity ?? e1Entity);
    if (!otherEntity) continue;
    const otherRole = isE1Email
      ? mc.entity_two_role?.display_name
      : mc.entity_one_role?.display_name;
    out.push({
      id: mc.id,
      entity: otherEntity,
      role: otherRole ?? undefined,
      remarks: mc.remarks ?? undefined,
      linkedAt: mc.created_at ?? new Date().toISOString(),
      endpoints: {
        one: { entity: e1Entity, role: mc.entity_one_role?.display_name ?? undefined },
        two: { entity: e2Entity, role: mc.entity_two_role?.display_name ?? undefined },
      },
    });
  }
  return out;
}
